import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  Divider,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import { useProfile } from '../../context/userContext';
import { useAuth } from '../../context/authContext';
import { useAuth0 } from '@auth0/auth0-react';
import { createOrganizationCheckout, checkOrganizationEligibility } from '../../network/apis/subscription.api';
import styles from '../../styles/OrganizationSignup.module.css';

const PlanConfirmation = ({ organizationData, selectedPlan, billingInterval, onConfirm, onBack }) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [preserveIndividual, setPreserveIndividual] = useState(true);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(true);
  const { profile } = useProfile();
  const { token } = useAuth();
  const { user: auth0User } = useAuth0();

  const selectedPrice = selectedPlan?.prices?.find(
    p => p.recurring?.interval === billingInterval
  );

  // Fetch eligibility data on mount
  useEffect(() => {
    const fetchEligibility = async () => {
      const userId = profile?.userId || auth0User?.sub;
      if (!userId || !token) return;

      try {
        const data = await checkOrganizationEligibility(token, userId);
        setEligibilityData(data);

        // If user has individual subscription, default to preserving it
        if (data?.currentSubscriptionStatus?.hasIndividualSubscription) {
          setPreserveIndividual(true);
        } else {
          setPreserveIndividual(false);
        }

        setLoadingEligibility(false);
      } catch (error) {
        setLoadingEligibility(false);
      }
    };

    fetchEligibility();
  }, [profile, auth0User, token]);

  const formatPrice = (amount, interval) => {
    if (!amount) return 'Custom';
    const price = interval === 'year' ? amount / 12 / 100 : amount / 100;
    return `$${Math.round(price)}`;
  };

  const formatTotalPrice = (amount, interval) => {
    if (!amount) return 'Custom';
    const total = amount / 100;
    return `$${total.toFixed(2)}`;
  };

  const handleProceedToPayment = async () => {
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    setProcessing(true);
    setError(null);

    try {

      // Use Auth0 sub as userId if profile doesn't have userId (for newly registered users)
      const userId = profile?.userId || auth0User?.sub;
      const email = profile?.email || auth0User?.email;

      if (!userId || !email) {
        throw new Error('User information not available. Please try logging in again.');
      }


      const checkoutData = {
        priceId: selectedPrice.id,
        userId: userId,
        organizationName: organizationData.name,
        organizationIdentifier: organizationData.identifier,
        email: email,
        preserveIndividualSubscription: preserveIndividual
      };


      // Create checkout session for organization subscription using API client
      const { url } = await createOrganizationCheckout(token, checkoutData);


      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      setError(error.message || 'Unable to proceed to payment. Please try again or contact support.');
      setProcessing(false);
    }
  };

  return (
    <div className={styles['plan-confirmation']}>
      <div className={styles['form-section']}>
        <h2 className={styles['section-title']}>
          Review Your Setup
        </h2>
        <p className={styles['section-description']}>
          Please review your organization details and selected plan before proceeding to payment
        </p>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} className={styles['error-alert']}>
            {error}
          </Alert>
        )}

        {/* Organization Details Summary */}
        <Card className={styles['summary-card']} elevation={2}>
          <CardContent>
            <div className={styles['summary-header']}>
              <BusinessIcon className={styles['summary-icon']} />
              <h3 className={styles['summary-title']}>
                Organization Details
              </h3>
            </div>

            <div className={styles['summary-content']}>
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Organization Name:</span>
                <span className={styles['summary-value']}>{organizationData.name}</span>
              </div>
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Identifier:</span>
                <span className={styles['summary-value']}>{organizationData.identifier}</span>
              </div>
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Type:</span>
                <span className={styles['summary-value']}>
                  {organizationData.type.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </span>
              </div>
              <div className={styles['summary-row']}>
                <span className={styles['summary-label']}>Team Size:</span>
                <span className={styles['summary-value']}>{organizationData.teamSize} members</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plan Summary */}
        <Card className={styles['summary-card']} elevation={2}>
          <CardContent>
            <div className={styles['summary-header']}>
              <PaymentIcon className={styles['summary-icon']} />
              <h3 className={styles['summary-title']}>
                Selected Plan
              </h3>
            </div>

            <div className={styles['plan-summary']}>
              <div className={styles['plan-summary-header']}>
                <h4 className={styles['plan-summary-name']}>{selectedPlan?.name}</h4>
                <div className={styles['plan-summary-price']}>
                  <span className={styles['price-amount']}>
                    {formatPrice(selectedPrice?.unit_amount, billingInterval)}
                  </span>
                  <span className={styles['price-period']}>/month</span>
                </div>
              </div>

              <p className={styles['plan-summary-description']}>
                {selectedPlan?.description}
              </p>

              <Divider className={styles['divider']} />

              <div className={styles['billing-summary']}>
                <div className={styles['summary-row']}>
                  <span className={styles['summary-label']}>Billing Frequency:</span>
                  <span className={styles['summary-value']}>
                    {billingInterval === 'month' ? 'Monthly' : 'Annual'}
                  </span>
                </div>
                <div className={styles['summary-row']}>
                  <span className={styles['summary-label']}>
                    {billingInterval === 'month' ? 'Monthly Total:' : 'Annual Total:'}
                  </span>
                  <span className={styles['summary-value-highlight']}>
                    {formatTotalPrice(selectedPrice?.unit_amount, billingInterval)}
                    {billingInterval === 'year' && '/year'}
                  </span>
                </div>
                {billingInterval === 'year' && (
                  <div className={styles['summary-row']}>
                    <span className={styles['summary-label']}>Effective Monthly:</span>
                    <span className={styles['summary-value']}>
                      {formatPrice(selectedPrice?.unit_amount, billingInterval)}/month
                    </span>
                  </div>
                )}
              </div>

              <Divider className={styles['divider']} />

              <div className={styles['features-preview']}>
                <h5 className={styles['features-preview-title']}>Key Features:</h5>
                <ul className={styles['features-preview-list']}>
                  {selectedPlan?.features?.slice(0, 5).map((feature, index) => (
                    <li key={index} className={styles['feature-preview-item']}>
                      <CheckCircleIcon className={styles['icon-check-small']} aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {selectedPlan?.features?.length > 5 && (
                    <li className={styles['feature-preview-item']}>
                      <span className={styles['more-features']}>
                        + {selectedPlan.features.length - 5} more features
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Individual Subscription Info (if exists) */}
        {loadingEligibility && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {!loadingEligibility && eligibilityData?.currentSubscriptionStatus?.hasIndividualSubscription && (
          <Card className={styles['summary-card']} elevation={2} sx={{ backgroundColor: '#f0f7ff' }}>
            <CardContent>
              <div className={styles['summary-header']}>
                <InfoIcon className={styles['summary-icon']} color="primary" />
                <h3 className={styles['summary-title']}>
                  Your Current Individual Subscription
                </h3>
              </div>

              <div className={styles['summary-content']}>
                <div className={styles['summary-row']}>
                  <span className={styles['summary-label']}>Plan:</span>
                  <span className={styles['summary-value']}>
                    {eligibilityData.currentSubscriptionStatus.individualSubscription.planName}
                  </span>
                </div>
                <div className={styles['summary-row']}>
                  <span className={styles['summary-label']}>Monthly Cost:</span>
                  <span className={styles['summary-value']}>
                    ${Number(eligibilityData.currentSubscriptionStatus.individualSubscription.price || 0).toFixed(2)}
                  </span>
                </div>
                <div className={styles['summary-row']}>
                  <span className={styles['summary-label']}>Status:</span>
                  <span className={styles['summary-value']}>
                    {eligibilityData.currentSubscriptionStatus.individualSubscription.status || 'Active'}
                  </span>
                </div>
              </div>

              <Divider className={styles['divider']} />

              {/* Preservation Checkbox */}
              <div className={styles['preservation-option']}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preserveIndividual}
                      onChange={(e) => setPreserveIndividual(e.target.checked)}
                      color="primary"
                      inputProps={{
                        'aria-label': 'Preserve individual subscription'
                      }}
                    />
                  }
                  label={
                    <Box>
                      <strong>Keep my individual subscription</strong>
                      <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '4px' }}>
                        {preserveIndividual
                          ? 'You will maintain both subscriptions and pay for each separately. This allows you to keep your personal workspace alongside your team workspace.'
                          : 'Your individual subscription will be cancelled when you create this organization subscription.'}
                      </div>
                    </Box>
                  }
                />
              </div>

              {/* Pricing Summary */}
              {preserveIndividual && (
                <Alert severity="info" sx={{ marginTop: '12px' }}>
                  <strong>Total Monthly Cost:</strong> $
                  {(
                    Number(eligibilityData.currentSubscriptionStatus.individualSubscription.price || 0) +
                    (selectedPrice?.unit_amount / 100 || 0)
                  ).toFixed(2)}
                  <div style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Individual: ${Number(eligibilityData.currentSubscriptionStatus.individualSubscription.price || 0).toFixed(2)} +
                    Organization: ${(selectedPrice?.unit_amount / 100 || 0).toFixed(2)}
                  </div>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trial Notice */}
        <Alert severity="info" className={styles['trial-notice']}>
          <strong>7-Day Free Trial:</strong> Your trial starts today. You won't be charged until after the trial period ends.
          Cancel anytime during the trial with no charge.
        </Alert>

        {/* Terms and Conditions */}
        <div className={styles['terms-container']}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                color="primary"
                inputProps={{
                  'aria-label': 'Accept Terms of Service and Privacy Policy'
                }}
              />
            }
            label={
              <span className={styles['terms-label']}>
                I agree to the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles['terms-link']}
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles['terms-link']}
                >
                  Privacy Policy
                </a>
              </span>
            }
          />
        </div>
      </div>

      <Box className={styles['form-actions']}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={processing}
          aria-label="Go back to plan selection"
          className={styles['btn-secondary']}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleProceedToPayment}
          disabled={!acceptedTerms || processing}
          aria-label="Proceed to secure payment"
          className={styles['btn-primary']}
        >
          {processing ? 'Processing...' : 'Proceed to Payment'}
        </Button>
      </Box>
    </div>
  );
};

export default PlanConfirmation;
